import {
  type ApolloClient,
  type ApolloError,
  type DocumentNode,
  type FetchPolicy,
  type OperationVariables,
  type WatchQueryFetchPolicy,
  useApolloClient,
  useQuery,
} from "@apollo/client";
import { useCallback } from "react";

import type { JobSeekerProfileResumeType } from "../../../__generated__/types.gqlgenerated";
import { fetchDualResumesEnabled, useDualResumesEnabled } from "./useDualResumesEnabled";

export interface DualResumeObjectsCandidate {
  id?: string | null;
  resumeType?: JobSeekerProfileResumeType | null;
}

/**
 * Which resume a read selects. Both stores must select the field the selector matches on.
 *
 * `resumeType` targets exactly one type; nothing of that type means `undefined`.
 *
 * `resumeTypeWithFallback` takes the types in preference order and returns the first that
 * matches — the whole list is searched for each type before the next is tried, so a later
 * type never beats an earlier one. For readers whose subject is "the account's main resume"
 * rather than one specific type, where an account can hold several. It is an explicit,
 * ordered preference, not the blind `[0]` fallback this module exists to remove.
 *
 * `resumeId` targets one exact record, for callers that already hold an id. FileSyncer mirrors
 * resume ids across the two stores for the duration of the migration, so an id resolves on
 * either branch. A nullish id matches nothing, so pair it with `skip` while the id is still
 * resolving.
 */
export type ResumeSelector =
  | { resumeType: JobSeekerProfileResumeType }
  | { resumeTypeWithFallback: readonly JobSeekerProfileResumeType[] }
  | { resumeId: string | null | undefined };

/** Either store's resume selection, for readers that gate both branches in one document. */
export interface DualResumeStores<TResume> {
  jobSeekerProfile?: { profile?: { resume?: readonly TResume[] | null } | null } | null;
  jobSeekerResumes?: { resumes?: readonly { resume?: TResume | null }[] | null } | null;
}

interface DualResumeObjectsReadDocuments {
  legacyDocument: DocumentNode;
  /**
   * Reads the resume off `jobSeekerResumes`. Must also select the same
   * `jobSeekerProfile.profile` fields as `legacyDocument` — `jobSeekerResumes`
   * exposes the resume body only.
   */
  dualDocument: DocumentNode;
  target: ResumeSelector;
  /**
   * Memoize this. The hooks key their result on it by reference, so a fresh object each render
   * hands back a fresh reader — and a caller that reloads from an effect keyed on that identity
   * gets a render loop rather than a wasted allocation.
   */
  variables?: OperationVariables;
}

export interface UseDualResumeObjectsReadOptions extends DualResumeObjectsReadDocuments {
  skip?: boolean;
  /**
   * Unset inherits the client's default — onboarding's is `no-cache`. Set it only on a
   * surface that relies on the shared normalized cache to see other surfaces' writes.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface DualResumeObjectsReadOutcome<TResume, TProfile> {
  resume: TResume | undefined;
  /** `jobSeekerProfile.profile` from the active branch, for non-resume fields. */
  profile: TProfile | undefined;
}

export interface DualResumeObjectsReadResult<
  TResume,
  TProfile,
> extends DualResumeObjectsReadOutcome<TResume, TProfile> {
  loading: boolean;
  /** The active branch's error. A failed flag is not an error — it reads legacy. */
  error?: ApolloError;
  /**
   * Re-runs the store this read resolved on, deliberately without re-resolving the gate: a screen
   * that opened against one store should keep reading it, since its writes are pinned to that
   * source and the two stores are not known to share resume ids.
   *
   * It must be the branch's own query. A separate read through {@link dualResumeObjectsRead}
   * writes the cache but leaves this hook's `error` set, so a retry built that way never dismisses
   * an error UI. Identity follows the active branch rather than being pinned, so a consumer that
   * feeds it to something identity-sensitive (`useFocusEffect`) should stabilize it there.
   */
  refetch: () => Promise<unknown>;
}

export interface DualResumeObjectsReadOptions extends DualResumeObjectsReadDocuments {
  fetchPolicy?: FetchPolicy;
}

/**
 * The lazy read's per-call arguments. `target` belongs here rather than on the hook:
 * an event-time read must select whatever the caller resolved by then, and keeping it
 * off the options leaves the returned callback referentially stable.
 */
export interface DualResumeObjectsReadRequest {
  target: ResumeSelector;
  /** Replaces the `variables` the hook was configured with. */
  variables?: OperationVariables;
}

export type UseDualResumeObjectsReadLazyOptions = Omit<DualResumeObjectsReadOptions, "target">;

/**
 * Both documents' shape. `legacyDocument` fills `jobSeekerProfile.profile.resume`,
 * `dualDocument` fills `jobSeekerResumes` — each selects the `TProfile` fields.
 */
interface ResumeReadQueryData<TProfile, TResume> {
  jobSeekerProfile?: {
    profile?: (TProfile & { resume?: readonly TResume[] }) | null;
  } | null;
  jobSeekerResumes?: {
    resumes?:
      | readonly {
          resume?: TResume | null;
        }[]
      | null;
  } | null;
}

/**
 * Reads one resume from whichever store the user is on, behind `allResumesEnabled`.
 * Only the selected branch runs, so a migrated user's payload is not doubled and the
 * legacy operation drops out of server-side traffic as the proctor ramps.
 */
export function useDualResumeObjectsRead<
  TResume extends DualResumeObjectsCandidate,
  TProfile = unknown,
>(options: UseDualResumeObjectsReadOptions): DualResumeObjectsReadResult<TResume, TProfile> {
  const { legacyDocument, dualDocument, target, variables, skip = false, fetchPolicy } = options;

  const { isDualResumeObjectsEnabled, loading: flagLoading } = useDualResumesEnabled({
    skip,
  });
  const flagResolved = !flagLoading;
  const useDualBranch = flagResolved && isDualResumeObjectsEnabled;

  const legacy = useQuery<ResumeReadQueryData<TProfile, TResume>>(legacyDocument, {
    variables,
    fetchPolicy,
    skip: skip || !flagResolved || useDualBranch,
  });
  const dual = useQuery<ResumeReadQueryData<TProfile, TResume>>(dualDocument, {
    variables,
    fetchPolicy,
    skip: skip || !flagResolved || !useDualBranch,
  });

  const active = useDualBranch ? dual : legacy;

  return {
    resume: selectResume({
      candidates: resumeCandidates<TResume>(active.data, useDualBranch),
      target,
    }),
    profile: active.data?.jobSeekerProfile?.profile ?? undefined,
    loading: flagLoading || active.loading,
    error: active.error,
    refetch: active.refetch,
  };
}

/**
 * {@link useDualResumeObjectsRead} outside React, for loaders. Rejects when the read fails,
 * including on GraphQL errors — callers await this and act on the resume, so a partial
 * response resolving to `resume: undefined` would read as "the account has none".
 */
export async function dualResumeObjectsRead<
  TResume extends DualResumeObjectsCandidate,
  TProfile = unknown,
>(
  client: ApolloClient<unknown>,
  options: DualResumeObjectsReadOptions,
): Promise<DualResumeObjectsReadOutcome<TResume, TProfile>> {
  const { legacyDocument, dualDocument, target, variables, fetchPolicy } = options;
  const useDualBranch =
    (await fetchDualResumesEnabled(client))?.isDualResumeObjectsEnabled ?? false;

  const { data } = await client.query<ResumeReadQueryData<TProfile, TResume>>({
    query: useDualBranch ? dualDocument : legacyDocument,
    variables,
    fetchPolicy,
    // Pinned, not inherited: the shared OneGraph client defaults to `all`, which resolves
    // GraphQL errors as partial data. Onboarding overrides that to `none` app-wide (RN-4120)
    // but myjobs and the glow-up surfaces do not, so the rejection above is only a contract
    // if this helper sets it itself.
    errorPolicy: "none",
  });

  return {
    resume: selectResume({
      candidates: resumeCandidates<TResume>(data, useDualBranch),
      target,
    }),
    profile: data?.jobSeekerProfile?.profile ?? undefined,
  };
}

/**
 * {@link useDualResumeObjectsRead} for React call sites that read on an event. The
 * selector is supplied per call, so a target resolved after render is always the one
 * read — see {@link DualResumeObjectsReadRequest}.
 */
export function useDualResumeObjectsReadLazy<
  TResume extends DualResumeObjectsCandidate,
  TProfile = unknown,
>(
  options: UseDualResumeObjectsReadLazyOptions,
): (
  request: DualResumeObjectsReadRequest,
) => Promise<DualResumeObjectsReadOutcome<TResume, TProfile>> {
  const client = useApolloClient();
  const { legacyDocument, dualDocument, variables, fetchPolicy } = options;

  return useCallback(
    (request: DualResumeObjectsReadRequest) =>
      dualResumeObjectsRead<TResume, TProfile>(client, {
        legacyDocument,
        dualDocument,
        target: request.target,
        variables: request.variables ?? variables,
        fetchPolicy,
      }),
    [client, legacyDocument, dualDocument, variables, fetchPolicy],
  );
}

/**
 * The resumes to choose from, from whichever store the gate selected. Exported for readers
 * that gate both branches in a single document and so cannot use the hooks above.
 */
export function resumeCandidates<TResume>(
  data: DualResumeStores<TResume> | null | undefined,
  useResumeCollection: boolean,
): readonly TResume[] {
  return useResumeCollection
    ? (data?.jobSeekerResumes?.resumes ?? []).flatMap((bundle) => bundle.resume ?? [])
    : (data?.jobSeekerProfile?.profile?.resume ?? []);
}

export function selectResume<TResume extends DualResumeObjectsCandidate>({
  candidates,
  target,
}: {
  candidates: readonly TResume[];
  target: ResumeSelector;
}): TResume | undefined {
  if ("resumeId" in target) {
    // A nullish id must never match a resume that left the field unselected or null — that
    // would return an arbitrary entry, the blind `[0]` bug in disguise.
    return target.resumeId == null
      ? undefined
      : candidates.find((resume) => resume.id === target.resumeId);
  }

  const types =
    "resumeTypeWithFallback" in target ? target.resumeTypeWithFallback : [target.resumeType];

  for (const resumeType of types) {
    const match = candidates.find((resume) => resume.resumeType === resumeType);
    if (match) {
      return match;
    }
  }

  return undefined;
}
